
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {Line2} from "../../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {setLabelWanted} from "../labels/labelState.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";

// Matches the yellow already used elsewhere for auxiliary/helper (not-
// directly-captured) geometry - see HeightAuxiliaryVisual.js's own
// AUX_COLOR/pipeTool.js's DIRECTION_COLOR family.
const AUX_COLOR = 0xffdc00;

function createTickLine(){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({color: AUX_COLOR, linewidth: 2, resolution: new THREE.Vector2(1000, 1000)});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.visible = false;
	return line;
}

// Visual aid for "Tværprofiler" (see tools/crossSectionTool.js): given the
// computed stations (position + tangent + name, see
// geometry/crossSectionGeometry.js), shows WHERE each profile is in the
// point cloud (task section 5 - "vis hvor hvert enkelt profil ligger") - a
// short perpendicular tick at the station's own true profileLength, plus
// its "P01"-style name.
//
// Same "purely decorative child of the real captured feature" convention
// HeightAuxiliaryVisual.js already established: not a registered Feature
// itself, added via `visual.add(markers)` and wired through the generic
// `visual.userData.auxiliaryVisual` hook (FeatureVisual.setLabelsVisible()/
// DigitizationModule's own update() loop already drive both the labelsVisible
// cascade and the per-frame update(camera,w,h) call through that one hook,
// with no Tværprofiler-specific code needed in either place).
export class CrossSectionMarkersVisual extends THREE.Object3D {
	constructor(){
		super();
		this.ticks = [];
		this.labels = [];
		this.labelsVisible = true;
	}

	// Builds one tick + one label per station. Called once, right after the
	// stations/profiles are computed - not per-frame.
	setStations(stations, profileLength){
		for(const tick of this.ticks) this.remove(tick);
		for(const label of this.labels) this.remove(label);
		this.ticks = [];
		this.labels = [];

		const half = profileLength / 2;

		for(const station of stations){
			const [tx, ty] = station.tangent;
			const perpX = -ty, perpY = tx;
			const [sx, sy, sz] = station.position;

			const tick = createTickLine();
			tick.position.set(sx - perpX * half, sy - perpY * half, sz);
			tick.geometry.setPositions([0, 0, 0, perpX * profileLength, perpY * profileLength, 0]);
			tick.geometry.computeBoundingSphere();
			tick.visible = true;
			this.add(tick);
			this.ticks.push(tick);

			const label = createStyledLabel(station.name);
			label.position.set(sx, sy, sz);
			setLabelWanted(label, this.labelsVisible);
			this.add(label);
			this.labels.push(label);
		}
	}

	setLabelsVisible(visible){
		this.labelsVisible = visible;
		for(const label of this.labels) setLabelWanted(label, visible);
	}
	// Labels, som LabelManager (labels/) styrer: hjælpelabels udfases tidligt og vises kun ved valg/nærzoom (LABEL-STRATEGI.md 12.4)
	auxLabels(){
		return this.labels;
	}

	updateResolution(width, height){
		for(const tick of this.ticks) tick.material.resolution.set(width, height);
	}

	// Single per-frame entry point, called via the generic
	// `userData.auxiliaryVisual.update(...)` hook - see HeightAuxiliaryVisual.js's
	// own doc comment for why this is a duck-typed hook rather than
	// FeatureVisual/DigitizationModule baking in Tværprofiler-specific code.
	update(camera, clientWidth, clientHeight){
		this.updateResolution(clientWidth, clientHeight);

		for(const label of this.labels){
			if(!label.visible) continue;
			const scale = computeScreenSpaceScale(
				camera, label.getWorldPosition(new THREE.Vector3()), camera.position,
				clientWidth, clientHeight, ScreenSpaceSizes.label);
			label.scale.set(scale, scale, scale);
		}
	}

	// GPU-resource teardown, called generically via the `userData.auxiliaryVisual`
	// hook from FeatureVisual.dispose() when the owning Tværprofiler feature
	// is deleted.
	dispose(){
		for(const tick of this.ticks){
			tick.geometry.dispose();
			tick.material.dispose();
		}
		for(const label of this.labels){
			label.dispose();
		}
	}
}
